import { Injectable } from '@angular/core';

import {
    IAdvancedSearchData, IAzavistaGroupedFieldsComponentData, IField, IRowAction
} from '@azavista/components/shared';
import { IApiClient } from '@azavista/servicelib';
import { ApiClientRowActionId } from './api-clients.interfaces';
import { IConfirmationDialogComponentData } from '../../shared/confirmation-dialog/confirmation-dialog.interfaces';
import {
    IColumnsListSetting, IconName, RequiredScopesService, SettingEntityType, SettingsService, SharedService, TranslationKey
} from '../../shared';

@Injectable({
    providedIn: 'root'
})
export class ApiClientsService {

    constructor(private sharedSvc: SharedService, private rsSvc: RequiredScopesService, private settingsSvc: SettingsService) {
    }

    async showDeleteApiClientConfirmation(): Promise<IConfirmationDialogComponentData> {
        return this.sharedSvc.showConfirmationDialog(
            this.sharedSvc.translate(TranslationKey.deleteApiClientDialogTitle),
            [
                this.sharedSvc.translate(TranslationKey.deleteApiClientDialogAreYouSure),
                this.sharedSvc.translate(TranslationKey.wontBeAbleToRestoreData)
            ]
        );
    }

    getApiClients(ids: number[], apiClients: IApiClient[]): IApiClient[] {
        const result = apiClients.filter(x => ids.indexOf(x.id as number) >= 0);
        return result;
    }

    getApiClientActions(): IRowAction[] {
        const requiredScopes = [this.rsSvc.getScopeA()];
        const result: IRowAction[] = [
            {
                label: this.sharedSvc.translate(TranslationKey.viewDetailsMenuAction),
                id: ApiClientRowActionId.see_details, icon: IconName.visibility, singleSelectionOnly: true,
                requiredScopes: requiredScopes
            },
            {
                label: this.sharedSvc.translate(TranslationKey.assignToTeamMenuAction),
                id: ApiClientRowActionId.assign_to_team, icon: IconName.people, singleSelectionOnly: false,
                requiredScopes: requiredScopes
            },
            {
                label: this.sharedSvc.translate(TranslationKey.deleteMenuAction),
                id: ApiClientRowActionId.delete, icon: IconName.delete, singleSelectionOnly: true,
                requiredScopes: requiredScopes
            }
        ];
        return result;
    }

    getAdvancedSearchData(columns: IField[]): IAdvancedSearchData {
        this.sharedSvc.sortAlphabetically(columns, (item) => item.label);
        const result: IAdvancedSearchData = {
            advancedFilters: [],
            allFields: columns
        };
        return result;
    }

    createNewApiClientGroupData(): IAzavistaGroupedFieldsComponentData {
        const result: IAzavistaGroupedFieldsComponentData = {
            expandPanels: true,
            editModeForAllFields: true,
            singleColumn: true,
            showActionButtons: true,
            fieldsWithEntity: {
                entity: {
                    name: null,
                } as IApiClient,
                fields: this.getEditableApiClientFields()
            }
        };
        return result;
    }

    async getTeamColumnsSettings(): Promise<IColumnsListSetting> {
        const allowedIds = this.sharedSvc.getTeamColumns().map(x => x.id);
        const defaultIds = allowedIds;
        const columnsSettings = await this.settingsSvc.getGlobalColumnsSettings(SettingEntityType.team, allowedIds, defaultIds);
        return columnsSettings;
    }

    getEditableApiClientFields(): IField[] {
        return this.getApiClientColumns();
    }

    getApiClientDisplayedColumns(): string[] {
        return ['name'];
    }

    getApiClientColumns(): IField[] {
        const result: IField[] = [];
        const category = this.sharedSvc.translate(TranslationKey.apiClientFields);
        result.push({
            id: 'name', name: 'name', label: this.sharedSvc.translate(TranslationKey.name), category: category,
            schema: { type: 'string' }, editable: true, builtin: true, visible_for_planners: true
        });
        return result;
    }
}
